import {differenceInCalendarDays,isValid,format} from "date-fns"
import { parsePhoneNumberFromString, getCountryCallingCode } from 'libphonenumber-js'
import DOMPurify from 'dompurify'
import api from '@/api'

// Every email field, sign-in included. The same rule as the server's company and integrator emails (Organization
// model): `+` and long domain endings are fine, and the domain needs a dot. The User model's rule is the same without
// the dot. The old pattern refused `+` and any domain ending longer than 4 letters.
export const isEmailValid = (val)=>{
    return val && val.match(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/)
    ? true
    : false
}

export const isURLValid = (val)=>{
    return val && val.match(/^(http(s)?:\/\/)?([\w-]+\.)+[\w-]+(\/(?!\/)[\w-.?%&=/]*)?$/)
    ? true
    : false
}

export const isValidEIN = (val)=> {
    return val && val.match(/^\d{2}-\d{7}$/)
    ? true
    : false
}

export const isValidDuns =  (val) => {
    return val && val.match(/^\d{9}$/)
    ? true
    : false
}

export const formatCurrency = (val, minLimit = 0, maxLimit = 50000000, allowDecimals = false) => {
        if(!val) {return (0).toFixed(allowDecimals ? 2 : 0);}
    // Remove any characters that are not digits or decimal points
        let value = String(val).replace(/[^\d.]/g, '');

        // Convert the value to a number. If it's not a number, set it to the minimum limit
        let number = value ? parseFloat(value) : minLimit;

        // Round the number to two decimal places if allowDecimals is true
        number = allowDecimals ? Math.round(number * 100) / 100 : Math.round(number);

        // Check for minimum and maximum limits
        if (number < minLimit) {
            number = minLimit;
        } else if (number > maxLimit) {
            number = maxLimit;
        }

        // Format the number with commas and optionally with two decimal places
        return allowDecimals ? number.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const formatUSCurrency = (val,max) => {
        let input = val.replace(/[^\d.]+/g, '')
        if(max && Number(input) > max) {input = `${max}`}
        const parts = input.split('.')
        const dollars = parts[0] ? parts[0].toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","): parts[0]
        const cents = parts[1] ? parts[1].length > 2 ? parts[1].slice(0,2) : parts[1] : parts[1]
        return input.includes('.') ? `${dollars}.${cents}` : `${dollars}`
}

export const formatPercentage = (val,max=100)=> {
    let input = val.replace(/[^\d.]+/g, '')
    if(Number(input) > max) {input = `${max}`}
    if(Number(input) < 0) {input = `0`}
    const parts = input.split('.')
    const pct = parts[0]
    const dec = parts[1] ? parts[1].length > 2 ? parts[1].slice(0,2) : parts[1] : parts[1]
    return input.includes('.') ? `${pct}.${dec}` : `${pct}`
}

export const formatNumberInput = (val,min=0,max=100)=> {
    if(!val) { return `${min}` }
    let input = val.replace(/[^\d]+/g, '')
    if(Number(input) > max) {input=`${max}`}
    if(Number(input) < min) {input=`${min}`}
    input = Math.round(Number(input))
    return `${input}`
}

export const formatInputNumber = (val,min=0,max=100)=>{
    if(!val) {return null}
    let input = val.replace(/[^\d]+/g, '')
    input = Math.round(Number(input))
    if(Number(input) > max) {input=`${max}`}
    if(Number(input) < min) {input=`${min}`}
    return input
}

// Compose a box's display label. Warehouse-type projects number boxes per-year, so a box carries a
// boxYear and shows as "YY-boxNumber" (e.g. 26-5). Legacy / non-warehouse boxes have no boxYear and
// show the bare number. Pass equipment.boxNumber and equipment.boxYear.
export const formatBoxNumber = (boxNumber, boxYear) => {
    // 0 is the "unassigned" sentinel (schema default); treat it and null/blank/NaN as no box.
    if (boxNumber === null || boxNumber === undefined || boxNumber === '' || Number(boxNumber) === 0 || Number.isNaN(Number(boxNumber))) { return '' }
    return boxYear ? `${String(boxYear).slice(-2)}-${boxNumber}` : `${boxNumber}`
}

// A room's display label, the app's one room-name standard: Room Number (nameOne) / Room Name (nameTwo) / Room Type.
// Missing parts are skipped and a part repeating the one before it is shown once (Room Name is often a copy of the type).
// Never the estimate number. '' only for a room with none of the three, which roomHasAName keeps from being saved.
// Display only — never a stored value or a match key. Box labels print it on one truncated line. Server twin: roomLabel
// in the backend's shared/roomGuards.js.
export const roomLabel = (room) => {
    const names = []
    for (const part of [room?.nameOne, room?.nameTwo, room?.roomType]) {
        const name = String(part ?? '').trim()
        if (name && name.toLowerCase() !== names[names.length - 1]?.toLowerCase()) names.push(name)
    }
    return names.join(' / ')
}

// A room needs any one of Room Number, Room Name or Room Type. Server twin: roomHasAName in shared/roomGuards.js.
export const roomHasAName = (room) => [room?.nameOne, room?.nameTwo, room?.roomType].some((part) => String(part ?? '').trim())

export const formatEIN = (val)=>{
         // Remove any characters that are not digits
         let input = val.replace(/[^\d]/g, '');

         // Automatically add hyphen after second digit
         if (input.length > 2) {
           input = input.slice(0, 2) + '-' + input.slice(2);
         }
   
         // Limit the length to 10 characters (XX-XXXXXXX)
         return input.slice(0, 10);
}

export const formatDuns = (val) => {

    // Remove any characters that are not digits
    let input = val.replace(/[^\d]/g, '');

    // Limit the length to 9 characters
    if (input.length > 9) {
      input = input.slice(0, 9);
    }

    // Update the DUNS number
    return input
}

export const showAsUSCurrency = (val)=>{
    const formattedString = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(val);
    return formattedString.replace('$', '$ ')
}

// Whole dollars, no space after the sign: $1,234,567. For report figures where cents are noise.
export const showAsWholeDollars = (val) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
}).format(Number(val) || 0)

// Phones. One stored shape (the server normalizes every save — backend shared/phone.js is the twin):
//   { countryAbbr:'US', countryCode:'+1', phoneNumber:'+15551234567' (E.164), rawValue:'5551234567', phoneExtension }
// Site contacts store the E.164 string in `phone` with the extension in its own field. Older records hold one of
// three earlier shapes (maska '555-123-4567', V1 '+1…'/national, V2 '(555) 123-4567'/'+1…'); parsePhone reads them all.
// Customer ship-to phones (customerPhone) are NOT phones in this sense — shown exactly as typed.
const phoneDigits = (v) => String(v ?? '').replace(/\D/g, '')
const phoneRegion = (abbr) => {
    const a = String(abbr || '').toUpperCase()
    return a === 'UK' ? 'GB' : (a || 'US')
}

// Any stored shape, or typed / pasted text. Null when there is no possible number in it.
export const parsePhone = (value, defaultCountry = 'US') => {
    let candidates = []
    let region = defaultCountry
    let extension = ''
    if (typeof value === 'string') {
        candidates = [value]
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
        region = phoneRegion(value.countryAbbr || defaultCountry)
        extension = phoneDigits(value.phoneExtension)
        const pn = String(value.phoneNumber ?? '').trim()
        const raw = String(value.rawValue ?? '').trim()
        const cc = phoneDigits(value.countryCode)
        // Both fields are tried: the old inputs could cut rawValue short while phoneNumber still held the whole number
        candidates = [raw.startsWith('+') ? raw : null, pn.startsWith('+') ? pn : null,
            ...[phoneDigits(raw), phoneDigits(pn)].filter(Boolean).flatMap((local) => [cc ? `+${cc}${local}` : null, local])]
    }
    for (const text of candidates.filter(Boolean)) {
        let n = null
        try { n = parsePhoneNumberFromString(text, region) } catch (e) { n = null }
        if (!n || !n.isPossible()) continue
        // +1 area codes start 2-9 (the server rejects the rest too); '192 380 9383' passes isPossible on length alone
        if (n.countryCallingCode === '1' && !/^[2-9]/.test(n.nationalNumber)) continue
        // Keep the stored (or picked) country when it shares the number's calling code — a Canadian 877 number stays
        // CA, and +1 numbers follow the country chosen in the input
        const preferred = typeof value === 'string' ? defaultCountry : value.countryAbbr
        let keepAbbr = false
        try { keepAbbr = !!preferred && getCountryCallingCode(phoneRegion(preferred)) === n.countryCallingCode } catch (e) { keepAbbr = false }
        return {
            countryAbbr: keepAbbr ? String(preferred).toUpperCase() : (n.country || (n.countryCallingCode === '1' ? 'US' : '')),
            countryCode: `+${n.countryCallingCode}`,
            e164: n.number,
            nationalDigits: n.nationalNumber,
            extension: extension || phoneDigits(n.ext),
            number: n.countryCallingCode === '1' ? n.formatNational() : n.formatInternational(),
        }
    }
    return null
}

// The stored phone object for a parsed number (what PhoneInput emits; the server normalizes the same way).
export const phoneObject = (parsed, extension = '', phoneType = null) => {
    if (!parsed) return null
    const out = {
        countryAbbr: parsed.countryAbbr,
        countryCode: parsed.countryCode,
        phoneNumber: parsed.e164,
        rawValue: parsed.nationalDigits,
        phoneExtension: phoneDigits(extension) || null,
    }
    if (phoneType) out.phoneType = phoneType
    return out
}

// (555) 123-4567 x123 for +1 numbers, +82 10-1234-5678 otherwise. `extension` is for site contacts, whose extension
// lives in its own field. A stored value that does not parse (legacy junk, a 1xx area code) still shows in the same
// format when it is 10 digits, otherwise as stored — never vanishing.
export const displayPhone = (value, extension = null) => {
    const p = parsePhone(value)
    if (!p) {
        const text = typeof value === 'string' ? value : String(value?.rawValue || value?.phoneNumber || '')
        let digits = phoneDigits(text)
        if (digits.length === 11 && digits[0] === '1') digits = digits.slice(1)
        const ext = phoneDigits(extension) || phoneDigits(value?.phoneExtension)
        const shown = digits.length === 10 ? digits.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3') : text
        return shown && ext ? `${shown} x${ext}` : shown
    }
    const ext = phoneDigits(extension) || p.extension
    return ext ? `${p.number} x${ext}` : p.number
}

// tel: link — E.164, the extension after a pause (",") so phones dial it
export const phoneHref = (value, extension = null) => {
    const p = parsePhone(value)
    if (!p) return null
    const ext = phoneDigits(extension) || p.extension
    return `tel:${p.e164}${ext ? `,${ext}` : ''}`
}

// mailto: link. Each part around the @ is encoded, so a stored address carrying `?` or `&` can never add a hidden
// recipient, subject or body; an ordinary address comes out unchanged (`+` becomes %2B, which mail clients read as +).
export const mailtoHref = (email) => {
    if (typeof email !== 'string' || !email.trim()) return null
    return `mailto:${email.trim().split('@').map(encodeURIComponent).join('@')}`
}

export const getAge = (date)=>{
    if(!date || !isValid(new Date(date))) {
        return 0
    } else {
        let compareDate = new Date(date)
        return differenceInCalendarDays(new Date(),compareDate)
    }
    
}

export const openDetails = (target)=> {
    if(target) {
        const nextElement = target.nextElementSibling;
        nextElement
        ? nextElement.classList.toggle('hidden')
        : null
    } else {
        console.log('No target')
    }
}

export const copyToClipboard = async (val)=>{
    try {
        await navigator.clipboard.writeText(val);
    } catch (err) {
        console.error('Failed to copy: ', err);
    }
}

// Random v4 UUID. crypto.randomUUID needs a 2022+ browser on HTTPS or localhost; anywhere it is missing, the same
// format is built from crypto.getRandomValues. Kit ids made here are stored, so the format must not change.
export const newUuid = ()=>{
    if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID()
    const b = crypto.getRandomValues(new Uint8Array(16))
    b[6] = (b[6] & 0x0f) | 0x40
    b[8] = (b[8] & 0x3f) | 0x80
    const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('')
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

// Allowlist for note / SOW HTML rendered with v-html. Covers what the TipTap toolbar can
// produce, plus the legacy inline tags (b, i, u, div) that older stored notes may still carry.
// renderNoteHtml below applies it; the backend's NOTE_SANITIZE_OPTIONS is its twin.
export const NOTE_HTML_CONFIG = {
    ALLOWED_TAGS: [
        'p','div','br','span',
        'strong','b','em','i','u','s','mark',
        'code','pre','blockquote',
        'ul','ol','li',
        'h1','h2','h3','h4','h5','h6',
        'hr','a'
    ],
    // style carries text-align and the indent margin; class carries a NOTE_COLORS class;
    // data-doc-id makes a link a project document (see openProjectDocument)
    ALLOWED_ATTR: ['href','target','rel','style','class','data-doc-id','start']
}

// The colours Notes / SOW text can carry, stored as the class so each theme shows its own shade. `light` is the
// class's light-mode colour, written inline only on a copy that leaves the app, where main.css does not reach.
// `hue` is where a pasted colour snaps to.
export const NOTE_COLORS = [
    { className:'text-red',    label:'Red',    hue:0,   light:'#b91c1c' },
    { className:'text-amber',  label:'Amber',  hue:40,  light:'#b45309' },
    { className:'text-green',  label:'Green',  hue:130, light:'#15803d' },
    { className:'text-sky',    label:'Sky',    hue:200, light:'#0369a1' },
    { className:'text-indigo', label:'Indigo', hue:245, light:'#4338ca' }
]
const NOTE_COLOR_CLASSES = NOTE_COLORS.map(x=>x.className)
// .tiptap mark's light-mode yellow, for the same copy
const NOTE_HIGHLIGHT_LIGHT = '#fef08a'

// Any CSS colour to {h,s,l,a}. A canvas parses it, so hex, rgb(), hsl() and names all work; an unparseable
// value leaves fillStyle unchanged, which the sentinel catches.
let colorContext = null
const toHsl = (value)=>{
    if(typeof value !== 'string' || !value.trim()) {return null}
    colorContext = colorContext || document.createElement('canvas').getContext('2d')
    if(!colorContext) {return null}
    colorContext.fillStyle = '#010203'
    colorContext.fillStyle = value.trim()
    const out = colorContext.fillStyle
    if(out === '#010203') {return null}

    let [r,g,b,a] = [0,0,0,1]
    if(out.startsWith('#')) {
        [r,g,b] = [1,3,5].map(i=>parseInt(out.slice(i,i+2),16))
    } else {
        const parts = out.match(/rgba?\(([^)]+)\)/)?.[1]?.split(',').map(Number)
        if(!parts) {return null}
        [r,g,b,a = 1] = parts
    }

    const [rn,gn,bn] = [r/255,g/255,b/255]
    const max = Math.max(rn,gn,bn), min = Math.min(rn,gn,bn)
    const l = (max + min) / 2
    const d = max - min
    const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1))
    let h = 0
    if(d !== 0) {
        if(max === rn) {h = ((gn - bn) / d) % 6}
        else if(max === gn) {h = (bn - rn) / d + 2}
        else {h = (rn - gn) / d + 4}
        h = (h * 60 + 360) % 360
    }
    return {h,s,l,a}
}

// The NOTE_COLORS class nearest a pasted or legacy colour, or null. Black, white, greys and faint colours read in
// only one theme, so they map to nothing and the text takes the theme's own colour.
export const noteColorClass = (value)=>{
    const c = toHsl(value)
    if(!c || c.a < 0.3 || c.s < 0.25 || c.l < 0.12 || c.l > 0.92) {return null}
    const distance = (hue)=>{ const d = Math.abs(c.h - hue) % 360; return Math.min(d, 360 - d) }
    return NOTE_COLORS.reduce((best,x)=>distance(x.hue) < distance(best.hue) ? x : best).className
}

// A pasted background that reads as a highlighter mark. Pale tints (table shading) and greys do not.
export const isNoteHighlight = (value)=>{
    const c = toHsl(value)
    return !!c && c.a >= 0.3 && c.s >= 0.4 && c.l >= 0.2 && c.l <= 0.85
}

// The shape of a Google Drive file id; the backend checks the same before storing one
export const DRIVE_FILE_ID = /^[A-Za-z0-9_-]{10,100}$/

// Display sanitizer for Notes / SOW. Its own DOMPurify instance, so this hook never touches other sanitize calls.
// A legacy inline colour becomes the nearest class, and only NOTE_COLORS classes and the indent / alignment styles
// survive, so stored text shows correctly in both themes without a migration.
const notePurifier = DOMPurify(window)
notePurifier.addHook('afterSanitizeAttributes', (node)=>{
    if(!node?.getAttribute) {return}

    // every link opens in a new tab, including ones saved before the editor wrote target itself. A document link
    // is opened by noteDocumentLinkClick instead, so it gets no target; an id that is not a Drive id is dropped.
    if(node.tagName === 'A' && node.hasAttribute('href')) {
        if(node.hasAttribute('data-doc-id') && !DRIVE_FILE_ID.test(node.getAttribute('data-doc-id'))) {node.removeAttribute('data-doc-id')}
        if(node.hasAttribute('data-doc-id')) {
            node.removeAttribute('target')
        } else {
            node.setAttribute('target', '_blank')
            node.setAttribute('rel', 'noopener noreferrer nofollow')
        }
    }

    const kept = (node.getAttribute('class') || '').split(/\s+/).filter(x=>NOTE_COLOR_CLASSES.includes(x)).slice(0,1)
    if(!kept.length && node.style?.color) {
        const legacy = noteColorClass(node.style.color)
        if(legacy) {kept.push(legacy)}
    }
    kept.length ? node.setAttribute('class', kept[0]) : node.removeAttribute('class')

    if(node.hasAttribute('style')) {
        const style = [
            node.style.marginLeft ? `margin-left: ${node.style.marginLeft}` : null,
            node.style.textAlign ? `text-align: ${node.style.textAlign}` : null
        ].filter(Boolean).join('; ')
        style ? node.setAttribute('style', style) : node.removeAttribute('style')
    }
})

// Stored Notes / SOW HTML (escaped) to safe HTML for v-html
// The server caps a stored note or SOW item at 50,000 characters (noteSchema maxlength) and counts the STORED form:
// the HTML with & < > escaped, so formatting costs far more than the words. This measures the editor's HTML the same
// way, so the editor can warn before a save is refused. Slightly over, never under: the server's clean-up only removes.
export const NOTE_STORED_LIMIT = 50000
export const noteStoredLength = (html)=>typeof html === 'string' ? html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').length : 0
export const noteTooLong = (html)=>noteStoredLength(html) > NOTE_STORED_LIMIT

export const renderNoteHtml = (stored)=>notePurifier.sanitize(decodeHtml(stored || ''), NOTE_HTML_CONFIG)

// For a copy that leaves the app: each colour class also gets its light-mode colour inline and each highlight its
// yellow, since email, Word and Docs never see main.css. Light, because those are white pages. The class stays,
// so pasting back into a note keeps the exact colour. A link to an app page (a document link names its project)
// gets the full address, so it still goes somewhere from an email.
export const noteHtmlForCopy = (root)=>{
    root.querySelectorAll('a[href^="/"]:not([href^="//"])').forEach(el=>{
        el.setAttribute('href', `${window.location.origin}${el.getAttribute('href')}`)
    })
    root.querySelectorAll('[class]').forEach(el=>{
        const color = NOTE_COLORS.find(x=>el.classList.contains(x.className))
        if(color) {el.style.color = color.light}
    })
    root.querySelectorAll('mark').forEach(el=>{
        el.style.backgroundColor = NOTE_HIGHLIGHT_LIGHT
    })
    return root
}

// A project document opens through the app's own download route, because only the app's Drive account can reach
// the file. The route checks the user's project access and that the file is in this project's documents folder.
// PDFs, images and plain text open in a tab; anything else (Word, Excel, drawings) downloads, under the file's own
// name from the server rather than the words of the link.
export const openProjectDocument = async (projectId, fileId, name)=>{
    const response = await api.get(`projects/documents/download/${projectId}/${fileId}`, {responseType:'blob'})
    const served = /filename="([^"]+)"/.exec(response.headers?.['content-disposition'] || '')?.[1]
    viewOrDownloadFile(response.data, response.headers?.['content-type'], served || name || 'DLSTracker Document')
}

// The markup a document link is made of: data-doc-id names the file. Inside the app a click opens it. Outside (a SOW
// copied into an email) href opens the project page, whose ?doc= offers the same file once the user is signed in.
export const projectDocumentLinkHref = (projectId, fileId)=>`/project/${projectId}?doc=${encodeURIComponent(fileId || '')}`

// The project a document link's file belongs to, read from its href - a link pasted in from another project keeps
// that project's address. Falls back to the project on screen when the href names none.
export const documentLinkProjectId = (href, fallbackProjectId)=>{
    try {
        const id = /^\/project\/([0-9a-f]{24})$/i.exec(new URL(href || '', window.location.origin).pathname)?.[1]
        return id || fallbackProjectId
    } catch {
        return fallbackProjectId
    }
}

export const projectDocumentLinkHtml = (fileId, name, projectId)=>{
    const link = document.createElement('a')
    link.setAttribute('href', projectDocumentLinkHref(projectId, fileId))
    link.setAttribute('data-doc-id', fileId)
    link.textContent = name || 'Document'
    return link.outerHTML
}

// Puts a document link on the clipboard. Rich text (email, Word, a note) gets the named link; plain text (a chat, a
// text message) gets the full address, as any "Copy link" does. Both lead to the project page, which offers the file.
export const copyProjectDocumentLink = async (fileId, name, projectId)=>{
    const address = `${window.location.origin}${projectDocumentLinkHref(projectId, fileId)}`
    const holder = document.createElement('div')
    holder.innerHTML = projectDocumentLinkHtml(fileId, name, projectId)
    holder.firstChild.setAttribute('href', address)
    await navigator.clipboard.write([new ClipboardItem({
        'text/html': new Blob([holder.innerHTML], {type:'text/html'}),
        'text/plain': new Blob([address], {type:'text/plain'})
    })])
}

// A project's linkable documents (Drive files and saved links) for the Notes / SOW editor. The list shows the last
// copy at once and refreshes behind it every time it opens, so a typed @ is instant after the first time and a file
// added or removed in Documents shows the same moment. Loaded on first use, not before: most notes never link one.
const noteDocumentCache = new Map()
const noteDocumentLoads = new Map()

// the last list for this project, or null before the first load
export const cachedNoteDocuments = (projectId)=>noteDocumentCache.get(projectId) || null

export const loadNoteDocuments = (projectId)=>{
    if(noteDocumentLoads.has(projectId)) {return noteDocumentLoads.get(projectId)}

    const load = Promise.allSettled([
        api.get(`projects/documents/${projectId}`),
        api.get(`projects/documentLinks/${projectId}`)
    ]).then(([fileResult, linkResult])=>{
        const files = fileResult.status === 'fulfilled' && Array.isArray(fileResult.value.data?.data)
            // archived files and folders are not documents to link to
            ? fileResult.value.data.data.filter(x=>x.properties?.archived !== 'true' && x.mimeType !== 'application/vnd.google-apps.folder')
            : []
        const links = linkResult.status === 'fulfilled' && Array.isArray(linkResult.value.data?.data?.documentLinks)
            ? linkResult.value.data.data.documentLinks
            : []
        const failed = [fileResult, linkResult].find(x=>x.status === 'rejected')
        const result = { files, links, error: failed ? (failed.reason?.response?.data?.error || failed.reason?.message || 'Unable to load documents.') : null }
        // a failed load never replaces a good list
        if(!failed || !noteDocumentCache.has(projectId)) {noteDocumentCache.set(projectId, result)}
        return result
    }).finally(()=>{
        noteDocumentLoads.delete(projectId)
    })
    noteDocumentLoads.set(projectId, load)
    return load
}

// click listener for rendered Notes / SOW: a document link opens the file; every other click is left alone
export const noteDocumentLinkClick = (event, projectId, onError)=>{
    const link = event?.target?.closest?.('a[data-doc-id]')
    if(!link) {return}
    event.preventDefault()
    // the file downloads before it opens: a spinner on the link, and further clicks ignored, until it does
    if(link.classList.contains('is-busy')) {return}
    link.classList.add('is-busy')
    openProjectDocument(documentLinkProjectId(link.getAttribute('href'), projectId), link.getAttribute('data-doc-id'), link.textContent?.trim())
        .catch(async (err)=>{ if(onError) {onError(await extractErrorMessage(err, 'Unable to open that document.'))} })
        .finally(()=>{ link.classList.remove('is-busy') })
}

// copy listener for rendered Notes / SOW. Left to itself the browser writes the theme's computed styles, so a copy
// made in dark mode pastes as grey text on a dark panel. This writes the selection's own markup instead.
export const copyNoteSelection = (event)=>{
    const selection = window.getSelection()
    if(!selection || selection.isCollapsed || !event?.clipboardData) {return}
    const holder = document.createElement('div')
    for(let i = 0; i < selection.rangeCount; i++) {
        holder.appendChild(selection.getRangeAt(i).cloneContents())
    }
    noteHtmlForCopy(holder)
    event.clipboardData.setData('text/html', holder.innerHTML)
    event.clipboardData.setData('text/plain', selection.toString())
    event.preventDefault()
}

// WARNING: decodeHtml decodes HTML entities but does NOT sanitize against XSS.
// Never use the output of this function directly in v-html without wrapping with DOMPurify.sanitize().
export const decodeHtml = (html) => {
    if(!html) {return ''}
    const htmlEntitiesMap = {
        "&amp;": "&",
        "&lt;": "<",
        "&gt;": ">",
        "&quot;": "\"",
        "&#039;": "'",
        // Add more entities here if needed
    };

    return html.replace(/&amp;|&lt;|&gt;|&quot;|&#039;/g, match => htmlEntitiesMap[match]);
}

export const dateInputToISO = (val)=>{
    if(val && isValid(new Date(val))) {
        const selectedDate = new Date(val)
        const convertedDate = new Date(selectedDate.getTime() + selectedDate.getTimezoneOffset() *60000)
        return convertedDate
    } else {
        return null
    }
}

export const formatISODateforInput = (val) => {
    if (val && isValid(new Date(val))) {
        const newDate = new Date(val); // No need for parseISO if val is already ISO formatted
        const month = String(newDate.getMonth() + 1).padStart(2, '0');
        const day = String(newDate.getDate()).padStart(2, '0');
        return `${newDate.getFullYear()}-${month}-${day}`;
    } else {
        return null;
    }
}


const manufacturerKey = (item) =>
    ((item?.manufacturer ?? '').toString().trim().toLowerCase() || 'unknown');

export const groupItemsByManufacturer = (items) => {
    const sortedItems = [...items].sort((a, b) => {
        const manufacturerCompare = manufacturerKey(a).localeCompare(manufacturerKey(b));
        if (manufacturerCompare !== 0) return manufacturerCompare;
        return (a.boxNumber ?? 0) - (b.boxNumber ?? 0);
    });

    const manufacturers = [];

    sortedItems.forEach(item => {
        const key = manufacturerKey(item);
        let manufacturer = manufacturers.find(m => m._key === key);
        if (!manufacturer) {
            const displayName = camelCaseToLabel(key) ?? key;
            manufacturer = { _key: key, name: displayName, items: [] };
            manufacturers.push(manufacturer);
        }
        manufacturer.items.push(item);
    });
    return manufacturers;
}

export const getItemsByManufacturer = (equipment,type,groupMulti=true)=>{
    if(!Array.isArray(equipment)) {return equipment}
    let itemsToProcess = []

    switch(type) {
        case 'equipment':
            itemsToProcess = equipment.filter(item => !item.kitId)
            break;
        case 'kits':
            itemsToProcess = equipment.filter(item => item.kitId);
            break;
        case 'accessories':
            itemsToProcess = equipment.filter(item => !item.kitId);
            break;
        default:
            itemsToProcess = equipment
            break;
    }

    const groupedByManufacturer =  groupItemsByManufacturer(itemsToProcess)

    if(type === 'kits') {
        groupedByManufacturer.forEach(manufacturer => {
            const kitsWithItems = []; // Will hold kits with their related items nested

            // First, find all parent kits
            const parentKits = manufacturer.items.filter(item => item.isKit);

            parentKits.forEach(kit => {
            // Initialize the kit object with an empty 'items' array for its related items
            const kitWithRelatedItems = {
                ...kit,
                items: []
            };

            // Find and add related items (!isKit but matching kitId) to the parent kit
            manufacturer.items.forEach(item => {
                if (!item.isKit && item.kitId === kit.kitId) {
                kitWithRelatedItems.items.push(item);
                }
            });

            // Add the parent kit (with its related items nested) to the kitsWithItems array
            kitsWithItems.push(kitWithRelatedItems);
            });

            // Replace the manufacturer's items with the newly grouped kits and their items
            manufacturer.items = kitsWithItems;
        })
    } else {
        if(groupMulti) {

        groupedByManufacturer.forEach(manufacturer => {
            const newItems = []; // Will hold both individual items and grouped multi-box items
            const boxNumberGroups = {}; // Temporary storage for multi-box items

            manufacturer.items.forEach(item => {
                if (item.multiBox) {
                    // Group multiBox units by full box identity (boxYear + boxNumber). Warehouse
                    // projects reset numbering annually, so 26-5 and 27-5 share boxNumber 5 but are
                    // different boxes — keying on boxNumber alone would merge their units.
                    const boxKey = `${item.boxYear ?? ''}-${item.boxNumber}`
                    if (!boxNumberGroups[boxKey]) {
                    boxNumberGroups[boxKey] = {
                        boxNumber: item.boxNumber,
                        boxYear: item.boxYear ?? null,
                        items: [],
                        multiBox: true,
                        // Carried so pick/packing surfaces can say "one sealed carton, N units"
                        // rather than leaving a picker to read "Qty: 2" as two boxes to find.
                        boxSealed: !!item.boxSealed
                    };
                    }
                    boxNumberGroups[boxKey].items.push(item);
                } else {
                    // Items without multiBox: true remain individual
                    newItems.push(item);
                }
            });
            // Add grouped multi-box items to the newItems array
            Object.values(boxNumberGroups).forEach(group => newItems.push(group));

            // Replace the manufacturer's items with the new array
            manufacturer.items = newItems;
        });
        }
    } 
    return groupedByManufacturer
}

export const groupServices = (services,contract=true)=>{

    let contractServices = [...services].filter(x => contract ? x.document === 'contract' : x.document !== 'contract')
        
    contractServices.sort((a, b) => {
        if (a.document !== b.document) {
            return a.document.localeCompare(b.document);
        } else {
            return a.displayOrder - b.displayOrder;
        }
    });

    let groupData = {};

    // Group services while updating summaries at each level.
    contractServices.forEach(item => {
        let document = item.document || 'unidentified';
        if (!groupData[document]) {
            groupData[document] = {summary: {hours: 0, revenue: 0}, categories: {}};
        }

        let category = item.category;
        if (!groupData[document].categories[category]) {
            groupData[document].categories[category] = {summary: {hours: 0, revenue: 0}, services: {}};
        }

        // Update document-level summary.
        groupData[document].summary.hours += item.hours || 0;
        groupData[document].summary.revenue += item.revenue || 0;

        // Update category-level summary.
        groupData[document].categories[category].summary.hours += item.hours || 0;
        groupData[document].categories[category].summary.revenue += item.revenue || 0;

        let partNumber = item.partNumber;
        if (!groupData[document].categories[category].services[partNumber]) {
            groupData[document].categories[category].services[partNumber] = {summary: {hours: 0, revenue: 0}, items: []};
        }

        // Update part number-level summary.
        groupData[document].categories[category].services[partNumber].summary.hours += item.hours || 0;
        groupData[document].categories[category].services[partNumber].summary.revenue += item.revenue || 0;
        groupData[document].categories[category].services[partNumber].items.push(item);
    });

    return groupData
}

// Latitude FIRST. Stored GeoJSON coordinates are [lng, lat], so callers pass coordinates[1] then coordinates[0].
export const distanceBetweenPoints = (lat1,lon1,lat2,lon2,unit='M')=> {
    // Validate input parameters
    if (typeof lon1 !== 'number' || typeof lat1 !== 'number' ||
        typeof lon2 !== 'number' || typeof lat2 !== 'number' ||
        !['K', 'N', 'M'].includes(unit)) {
        console.error('Invalid input parameters to calculate distance.');
        return 0; // Indicate an error with the input.
    }

    // Check if the coordinates are the same, which would mean a distance of 0.
    if (lat1 === lat2 && lon1 === lon2) {
        return 0;
    }

    // Convert latitude and longitude from degrees to radians.
    var radLat1 = Math.PI * lat1 / 180;
    var radLat2 = Math.PI * lat2 / 180;
    var theta = lon1 - lon2;
    var radTheta = Math.PI * theta / 180;

    // Calculate the distance using the haversine formula.
    var dist = Math.sin(radLat1) * Math.sin(radLat2) + Math.cos(radLat1) * Math.cos(radLat2) * Math.cos(radTheta);
    dist = Math.min(dist, 1); // Ensure dist does not exceed 1 due to rounding errors.
    dist = Math.acos(dist);
    dist = dist * 180 / Math.PI;
    dist = dist * 60 * 1.1515; // Distance in miles.

    // Convert the distance to the requested unit.
    if (unit === "K") {
        dist *= 1.609344; // Convert to kilometers.
    } else if (unit === "N") {
        dist *= 0.8684; // Convert to nautical miles.
    }
    // No conversion needed if the unit is miles (M), as dist is already in miles.

    return parseFloat(dist.toFixed(1));
}

export const capitalizeFirstLetter = (str)=>{
    if(str && typeof str === 'string') {
        return str.charAt(0).toUpperCase() + str.slice(1)
    }
    return str
}

/**
 * Safely converts the entire string to uppercase.
 * Defensive: null/undefined returns ''; non-strings are coerced to string.
 */
export const capitalizeString = (str) => {
    if (str == null) return ''
    return String(str).toUpperCase()
}

/**
 * Get a value from an object by key. If key contains '.', treat as path (e.g. 'rackId.name').
 * Defensive: null/undefined obj or invalid key returns undefined.
 * Ensures path lookups return a comparable value (object values are not used for sort).
 */
export function getSortValue(obj, key) {
    if (obj == null || key == null) return undefined;
    if (typeof key !== 'string') return undefined;
    if (!key.includes('.')) return obj[key];
    const value = key.split('.').reduce((acc, segment) => (acc != null && typeof acc === 'object' ? acc[segment] : undefined), obj);
    // Paths like 'rackId.name' should resolve to a primitive; if still an object, use empty string so sort is stable
    return value != null && typeof value === 'object' ? '' : value;
}

export const naturalSort = (array, key1, key2 = null, order = 'asc', nullsLast = false) => {
    if (!Array.isArray(array)) return array;

    // key1 may be a single string (legacy 2-key path: key1 + optional key2) or an
    // array of keys for N-key sorting. Only the first key honors `order` — the
    // remaining keys always compare ascending, preserving the original behavior.
    const keys = Array.isArray(key1)
        ? key1.filter(k => k != null && k !== '')
        : [key1, key2].filter(k => k != null && k !== '');
    if (keys.length === 0) return array;

    const isEmpty = (v) => v == null || v === '' || v === 0;

    const compareNatural = (a, b) => {
        const aEmpty = isEmpty(a);
        const bEmpty = isEmpty(b);
        if (nullsLast && aEmpty !== bEmpty) return aEmpty ? 1 : -1;
        if (aEmpty) a = '';
        if (bEmpty) b = '';
        return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    };

    const multiplier = order === 'desc' ? -1 : 1;

    return array.sort((a, b) => {
        for (let i = 0; i < keys.length; i++) {
            const va = getSortValue(a, keys[i]);
            const vb = getSortValue(b, keys[i]);
            const cmp = compareNatural(va, vb) * (i === 0 ? multiplier : 1);
            if (cmp !== 0) return cmp;
        }
        return 0;
    });
};

export const sanitizeFileName = (name) => {
    return name.replace(/[/\\?%*:|"<>]/g, '-'); // Replace problematic characters with hyphens
};

export const camelCaseToLabel = (name)=>{
    if(typeof name !== 'string' || !name.trim()) {return null}
    if(name === 'pto') {return 'PTO'}
    return name
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Insert space before each capital letter
    .replace(/^./, str => str.toUpperCase()) // Capitalize the first letter
    .replace(/\b\w/g, char => char.toUpperCase());
}

export const camelCaseToAbbreviation = (str)=> {
    if (typeof str !== 'string' || !str) return '';
    return str
        .replace(/([a-z])([A-Z])/g, '$1 $2') // insert space before capital letters
        .split(' ')
        .map(word => word[0]?.toUpperCase() || '')
        .join('');
}

export const filterKeys = (object, keepZero = false) => {
    if (typeof object !== 'object' || object === null) return {};

    return Object.fromEntries(
        Object.entries(object).filter(([key, value]) => {
            if (key && typeof value === 'number') {
                return keepZero || value !== 0; // Keep zeros if 'keepZero' is true
            }
            return false; // Only keep numbers
        })
    );
};

export const openProject = async (id,modalToOpen=null)=>{
    const queryString = modalToOpen ? `?modalToOpen=${modalToOpen}` : '';
    window.open(`/project/${id}${queryString}`, '_blank');}

export const linkToGoogleMaps = async (googlePlacesId)=>{
    googlePlacesId
    ? window.open(`https://www.google.com/maps/place/?q=place_id:${googlePlacesId}`)
    : null
}

// Display lines for a stored address — mirrored by formatAddressLines in the backend's shared/constants.js. Each
// part is '' when blank, so callers never print "undefined". Google's short names (streetShort / stateShort,
// captured for US addresses) display when present; the stored street / state are never changed. `addressTwo` is
// the retired line-2 key: a non-empty one wins (it was the edited value wherever both exist), read until every
// collection is migrated to `address2`.
export const formatAddressLines = (address, { shortNames = true } = {}) => {
    const a = address && typeof address === 'object' ? address : {}
    const street = (shortNames && a.streetShort) || a.street
    const state = (shortNames && a.stateShort) || a.state
    const cityState = [a.city, state].filter(Boolean).join(', ')
    return {
        street: [a.street_number, street].filter(Boolean).join(' '),
        line2: a.addressTwo || a.address2 || '',
        cityStateZip: [cityState, a.postalCode].filter(Boolean).join(' ')
    }
}

// A typed ship-to (courier and order forms) filled from the address search. The street is written as one line
// ("123 Main Street"), so its short form carries the number too. A venue's name never goes into Street.
export const searchFilledParts = (address, isPointOfInterest) => {
    const a = address && typeof address === 'object' ? address : {}
    const street = isPointOfInterest ? '' : [a.street_number, a.street].filter(Boolean).join(' ')
    return {
        street,
        state: a.state || '',
        streetShort: street && a.streetShort ? [a.street_number, a.streetShort].filter(Boolean).join(' ') : '',
        stateShort: a.stateShort || ''
    }
}

// The short names to save with a typed ship-to: each only while its part still reads exactly as the search filled
// it, so a hand edit never leaves a stale short form on display.
export const searchedShortNames = (filled, current) => ({
    ...(filled?.stateShort && current?.state === filled.state ? { stateShort: filled.stateShort } : {}),
    ...(filled?.streetShort && current?.street === filled.street ? { streetShort: filled.streetShort } : {})
})

// Prevailing-wage payroll export: one line, full state name (timecard snapshots are excluded from short names).
export const formatGoogleAddressForExport = (address)=>{
    if(!address || typeof address !== 'object') {
        return 'Invalid Address'
    }
    const { street, line2, cityStateZip } = formatAddressLines(address, { shortNames: false })
    return [street, line2, cityStateZip].filter(Boolean).join(' ')
}

/**
 * Formats insurance requirement keys for display
 * Converts snake_case keys to Title Case with spaces
 * Handles parentheses by removing underscores only outside parentheses
 * 
 * @param {string} key - The insurance requirement key (e.g., "property_damage", "bodily_injury_(per_accident)")
 * @returns {string} - Formatted display text (e.g., "Property Damage", "Bodily Injury (per accident)")
 * 
 * @example
 * formatInsuranceKey("property_damage") // "Property Damage"
 * formatInsuranceKey("bodily_injury_(per_accident)") // "Bodily Injury (per accident)"
 * formatInsuranceKey("general_liability") // "General Liability"
 * formatInsuranceKey("workers_compensation") // "Workers Compensation"
 */
export const formatInsuranceKey = (key) => {
    if (!key || typeof key !== 'string') {
        return 'Unknown Coverage'
    }

    // Handle parentheses: remove underscores only outside parentheses
    let formatted = key
        .split('(')
        .map((part, index) => {
            if (index === 0) {
                // First part (before any parentheses): replace underscores with spaces
                return part.replace(/_/g, ' ')
            } else {
                // Parts inside parentheses: keep underscores as-is
                return part
            }
        })
        .join('(')

    // Capitalize first letter of each word
    formatted = formatted
        .split(' ')
        .map(word => {
            // Handle words that might have parentheses
            if (word.includes('(')) {
                const [beforeParen, afterParen] = word.split('(')
                return beforeParen.charAt(0).toUpperCase() + beforeParen.slice(1).toLowerCase() + 
                       (afterParen ? '(' + afterParen : '')
            } else {
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            }
        })
        .join(' ')

    return formatted
}

export const usStates =  [
    "NY", "NJ", "CT", "DE", "MA", "NA", "AL", "AK", "AZ", "AR", "CA", "CO", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NM", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
]

/** MIME types that browsers can display inline (view vs download). */
// Opened files are shown from a blob URL, which runs with this app's origin. SVG and HTML can carry
// script, so an uploaded file would execute here — an explicit list only, never an `image/` or
// `text/` prefix match. Anything not listed downloads instead of opening, which is always safe.
const VIEWABLE_MIME_TYPES = [
    'application/pdf',
    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp',
    'text/plain', 'text/csv',
    'application/json'
]

export const canViewInBrowser = (mimeType) => {
    if (!mimeType || typeof mimeType !== 'string') return false
    const normalized = mimeType.split(';')[0].trim().toLowerCase()
    return VIEWABLE_MIME_TYPES.includes(normalized)
}

// Rewrite a Google Drive thumbnailLink size token (e.g. "=s220" or "=w200-h200")
// to a larger bounding box so the same CDN image can serve a lightbox view
// without downloading the original blob. Returns the url unchanged if no token matches.
export const bumpThumbnailSize = (url, size = 1600) => {
    if (!url || typeof url !== 'string') return url
    const safeSize = Number.isFinite(size) && size > 0 ? Math.round(size) : 1600
    return url.replace(/=(?:s\d+|w\d+-h\d+)(-[^/]*)?$/, `=s${safeSize}`)
}

export const viewOrDownloadFile = (blob, mimeType, downloadFileName='DLSTracker Document') => {
    if (!blob) throw new Error('Invalid download file provided.')
    const type = (mimeType && typeof mimeType === 'string')
        ? mimeType.split(';')[0].trim().toLowerCase()
        : 'application/octet-stream'
    const blobWithType = blob instanceof Blob && blob.type
        ? blob
        : new Blob([blob], { type })
    const url = URL.createObjectURL(blobWithType)
    const canView = canViewInBrowser(type)

    const doDownload = (filename) => {
        const link = document.createElement('a')
        link.href = url
        link.download = filename || 'download'
        document.body.appendChild(link)
        link.click()
        setTimeout(() => {
            URL.revokeObjectURL(url)
            link.parentNode?.removeChild(link)
        }, 0)
    }

    if (canView) {
        const newWindow = window.open(url, '_blank')
        if (!newWindow) {
            doDownload(downloadFileName)
        }
    } else {
        doDownload(downloadFileName)
    }
}

// Pull a clean, human-readable message out of an axios error. Handles responseType:'blob' requests,
// whose error body comes back as a Blob (so `err.response.data.error` is undefined) — reads the blob
// text and parses the JSON error, with a fallback at every step. Central + defensive: never throws
// (a non-JSON body, an unreadable blob, or a null err all degrade to the fallback), so callers can
// safely `responseError.value = await extractErrorMessage(err)`.
export const extractErrorMessage = async (err, fallback = 'Something went wrong.') => {
    try {
        const data = err.response?.data
        if (data instanceof Blob) {
            let text = ''
            try { text = await data.text() } catch { text = '' }
            let parsed = null
            try { parsed = JSON.parse(text) } catch { parsed = null }
            return parsed?.error || err.message || fallback
        }
        return data?.error || err.message || fallback
    } catch {
        return err?.message || fallback
    }
}

// UPC / GTIN validation. Mirrors backend utils/upc.js so the catalog UI can give instant
// feedback; the server remains the authority (it re-validates and dup-checks on write).
const UPC_VALID_LENGTHS = [8, 12, 13, 14]

const computeUpcCheckDigit = (body) => {
    let sum = 0
    for (let i = 0; i < body.length; i++) {
        const digit = body.charCodeAt(body.length - 1 - i) - 48
        sum += digit * (i % 2 === 0 ? 3 : 1)
    }
    return (10 - (sum % 10)) % 10
}

export const isValidUpc = (code) => {
    if (typeof code !== 'string') return false
    if (!/^\d+$/.test(code)) return false
    if (!UPC_VALID_LENGTHS.includes(code.length)) return false
    const body = code.slice(0, -1)
    const check = code.charCodeAt(code.length - 1) - 48
    return computeUpcCheckDigit(body) === check
}

// Manufacturer near-match, for the new-item catalog form. Staff typing a manufacturer freehand invent
// near-duplicates ("crestron electronics" alongside "crestron"), so the form compares what they typed
// against every manufacturer already in the catalog and offers the existing one.
//
// Deliberately runs over the COMPLETE list rather than a server-side prefix or fuzzy query: at ~539
// distinct manufacturers the whole set is a few KB, and matching locally has no recall ceiling. Every
// variant worth catching is one a retrieval query would miss — a first-character typo ("krestron"),
// leading noise ("avi crestron"), or punctuation ("qs" vs "q-sys").
//
// What it does NOT catch: abbreviations and alternate trade names ("hp" / "hewlett-packard", "qsc" /
// "q-sys"). Edit distance can't reach those — a person fixes them with Fix Manufacturer on the Catalog page.

// Strip case, spacing and punctuation so "Q-SYS", "q sys" and "qsys" all compare equal. Comparison
// only — never write this back, the stored value is what the user typed.
export const squashManufacturer = (val) => typeof val === 'string'
    ? val.toLowerCase().replace(/[^a-z0-9]/g, '')
    : ''

// Mirrors shared/manufacturerGrouping.js on the backend, which uses the same distance for its typo
// pre-collapse. Keep the two in step — if one changes, the entry-time check and the reconciliation
// tool start disagreeing about what counts as a typo.
export const levenshtein = (a, b) => {
    if (a === b) return 0
    if (!a.length) return b.length
    if (!b.length) return a.length
    let prev = Array.from({ length: b.length + 1 }, (_, j) => j)
    let curr = new Array(b.length + 1)
    for (let i = 1; i <= a.length; i++) {
        curr[0] = i
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1
            curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost)
        }
        const swap = prev
        prev = curr
        curr = swap
    }
    return prev[b.length]
}

// Containment floor: below this, a short squashed string sits inside too many unrelated names.
const MFR_MIN_CONTAIN_LEN = 4

// Edit tolerance scales with length — one edit means very different things at 4 characters and at 12.
const mfrEditTolerance = (len) => len >= 8 ? 2 : 1

// Two different floors, because a half-typed prefix and a finished value are not the same question.
// A prefix is still being typed and lands in a list the user scans, so 3 characters is enough for
// "kre" to surface CRESTRON. A whole value is a verdict, and at 3 characters one edit separates real
// brands ("qsc" / "qse") — measured on the LONGER of the two so "shure"/"sure" still qualifies.
const MFR_MIN_PREFIX_TYPO_LEN = 3
const MFR_MIN_WHOLE_TYPO_LEN = 5

// Ranked existing manufacturers for a freehand value, closest first. ONE function behind both the
// typeahead and the blur check, so the dropdown can never offer a different answer than the warning.
//
// The reasons, strongest first:
//   exact       stored value is identical (nothing to suggest)
//   punctuation same once punctuation/spacing is stripped, stored differently — "q-sys" vs "qsys"
//   prefix      candidate starts with what's typed — ordinary typeahead
//   contains    one value sits inside the other — suffixes and leading noise, no suffix list needed
//   prefix-typo typed text is a near-miss of the candidate's opening run — catches "kres" -> CRESTRON
//               while the name is still half-typed, which plain containment cannot
//   typo        whole values are within the edit tolerance — "shure" vs "sure"
export const rankManufacturers = (typed, list) => {
    const squashed = squashManufacturer(typed)
    if (!squashed || !Array.isArray(list)) return []

    // Storage lowercases manufacturer, so a row differing only in case is the SAME value.
    const rawTyped = String(typed == null ? '' : typed).trim().toLowerCase()

    const out = []
    for (const row of list) {
        const candidate = squashManufacturer(row && row.manufacturer)
        if (!candidate) continue
        const raw = String(row && row.manufacturer != null ? row.manufacturer : '').trim().toLowerCase()

        let reason = null
        let rank = 0
        if (raw === rawTyped) { reason = 'exact'; rank = 0 }
        else if (candidate === squashed) { reason = 'punctuation'; rank = 1 }
        else if (candidate.startsWith(squashed)) { reason = 'prefix'; rank = 2 }
        else if (Math.min(candidate.length, squashed.length) >= MFR_MIN_CONTAIN_LEN
            && (candidate.includes(squashed) || squashed.includes(candidate))) { reason = 'contains'; rank = 3 }
        else {
            const tolerance = mfrEditTolerance(Math.min(candidate.length, squashed.length))
            // Still typing: compare against the candidate's opening run. Gated on the candidate being
            // genuinely LONGER, otherwise this is a whole-value comparison wearing a prefix costume and
            // it would rule "qse" a typo of "qsc".
            if (candidate.length > squashed.length && squashed.length >= MFR_MIN_PREFIX_TYPO_LEN
                && levenshtein(squashed, candidate.slice(0, squashed.length)) <= tolerance) {
                reason = 'prefix-typo'; rank = 4
            }
            // Whole values. Length gap alone can exceed the tolerance — skip the distance work then.
            else if (Math.max(candidate.length, squashed.length) >= MFR_MIN_WHOLE_TYPO_LEN
                && Math.abs(candidate.length - squashed.length) <= tolerance
                && levenshtein(squashed, candidate) <= tolerance) { reason = 'typo'; rank = 5 }
        }
        if (reason) out.push({ manufacturer: row.manufacturer, count: row.count || 0, reason, rank })
    }

    // Closest reason first, then whichever name more of the catalog already uses.
    out.sort((a, b) => a.rank - b.rank || b.count - a.count)
    return out
}

// Customer pickup vs courier, for FULFILLMENT ORDER shipments. The method is set at ingest from the
// order file's Shipment Type and is the ONLY thing that differs between the two — same states, same
// allocation, same packing list, same end-of-day report. Read it here rather than in each component so
// no surface invents its own test.
// NOT the same thing as an install pickup, which is method 'pickup' + locationType 'pickup' created in
// NewShipment.vue, has no order behind it and never appears on any of these surfaces. Every caller of
// this is already scoped to fulfillment orders (fulfillmentProjectId / isFulfillmentOrder), so method
// alone is sufficient here — do NOT reuse it on a surface that also lists install shipments.
export const isOrderPickup = (shipment) => shipment?.method === 'pickup'

// How the person collecting a customer pickup was identified. The KEYS mirror the Shipment schema enum
// and the transition route's whitelist — send the value, show the label. Kept here so the release dialog
// and the order view cannot drift on either half.
// We record the TYPE of identification and never an image of it: the responsibility we take on is that
// the check happened, not the safekeeping of somebody's licence. 'Known Client' is a real answer rather
// than a gap — a regular the warehouse recognises on sight, who the team would otherwise have no honest
// option for.
export const PICKUP_ID_TYPES = [
    { value: 'driversLicense', label: "Driver's License" },
    { value: 'stateId', label: 'State ID' },
    { value: 'companyBadge', label: 'Company Badge' },
    { value: 'knownClient', label: 'Known Client' }
]

export const pickupIdTypeLabel = (idType) => PICKUP_ID_TYPES.find(t => t.value === idType)?.label || ''

// Fulfillment order shipments: the single definition of "ready to ship". Lived in three components
// separately and drifted — the warehouse queue card said Ready on the label alone while the order view
// required all four, so the same shipment read differently depending on where you looked.
// Mirrors the transition route's 2 -> 3 checks exactly; change both together.
// A shipment on the recipient's or a third party's carrier account is never charged to us, so the
// transaction record carries no cost and the flag stands in for it.
// A customer pickup is never handed to a carrier, so there is no label to print, no tracking number to
// record and no freight cost to enter — the packing list is the only part of its gate that can be
// satisfied in advance, and it is what the customer signs at collection. The rest of that gate (the
// signed copy scanned back in, and the identity of whoever collected) is captured in the release dialog
// at the moment of handover, so it is deliberately NOT part of readiness: it cannot exist until someone
// is standing at the counter.
export const isShipmentReadyToShip = (shipment) => {
    if (isOrderPickup(shipment)) return !!shipment?.packingListId
    return !!(
        shipment?.packingListId
        && shipment?.shippingLabel?.driveFileId
        && (shipment?.estimatedShippingCost > 0 || shipment?.thirdPartyShipping === true)
        && String(shipment?.courierTrackingNumber || '').trim()
    )
}

// The method as a badge, or null for a courier order. Every surface that lists order shipments renders
// this next to the status chip so a pickup is identifiable at a glance — the physical handling differs
// (staged for collection rather than handed to a carrier) and nothing else on the row says so. Null
// rather than a "Courier" badge: courier is the default and badging it would be noise on every row.
// The label is "Customer Pickup", not "Pickup" — verbatim what the order file's Shipment Type carries,
// and deliberately distinct from an INSTALL pickup, which is a separate thing on separate surfaces.
// An INTERNAL TRANSFER is a pickup where the collector is us — the goods move to one of our own projects
// rather than leaving with the customer. It reads as its own label everywhere the pickup badge appears,
// because "Customer Pickup" on one is actively wrong: nobody is collecting it. Same chip colour, since it
// is still the not-a-carrier case and the colour is what makes that scannable down a list.
// Decided here rather than per component so the surfaces that render this cannot word it differently —
// the same reason blockingDocumentNames below lives in shared.
export const orderShipmentMethodChip = (shipment) => isOrderPickup(shipment)
    ? (shipment?.internalTransfer
        ? { label: 'Internal Transfer', chip: 'chip-indigo', text: 'text-indigo' }
        : { label: 'Customer Pickup', chip: 'chip-indigo', text: 'text-indigo' })
    : null

// The captured documents that block a hand-back, named. Mirrors blockingDocuments() in
// controllers/fulfillment.js — the server is what actually refuses; this exists so the control can say WHY
// before it is pressed rather than only after. Kept here rather than in each component so the two surfaces
// that render the padlock cannot word it differently.
// The packing list is deliberately absent: it is regenerated from contents and is discarded on unlock.
export const blockingDocumentNames = (shipment) => [
    shipment?.shippingLabel?.driveFileId ? 'shipping label' : null,
    shipment?.transactionRecord?.driveFileId ? 'transaction receipt' : null
].filter(Boolean)

// Why the hand-back is unavailable, as a tooltip. Null when it IS available, so callers can fall back to
// their own affirmative wording.
export const unlockBlockedReason = (shipment, canUnlock) => {
    if (!canUnlock) return 'Locked by warehouse'
    const docs = blockingDocumentNames(shipment)
    return docs.length ? `Remove the ${docs.join(' and ')} first` : null
}

// INSTALL courier shipments: what is still missing before it can ship, or null when it can. The install
// counterpart of isShipmentReadyToShip, deliberately separate so the fulfillment order rule never moves
// with it. Mirrors the 2 -> 3 checks in moveShipmentForward (controllers/shipments.js) in the same order —
// change both together. A reason rather than a boolean because the Ship tooltip has to say what is missing.
export const installCourierShipBlocker = (shipment) => {
    if (!shipment?.packingListId) return 'A packing list is required'
    if (!shipment?.shippingLabel?.driveFileId) return 'A shipping label is required'
    if (!String(shipment?.courierTrackingNumber || '').trim()) return 'Enter the tracking number'
    if (!String(shipment?.carrier || '').trim()) return 'Enter the carrier'
    // A Customer Stock request needs no cost (moveShipmentForward skips it for isCustomerHold — change both together).
    if (shipment?.projectId?.isCustomerHold !== true && shipment?.thirdPartyShipping !== true && !(shipment?.estimatedShippingCost > 0)) return 'Enter the shipping cost, or mark it as 3rd party'
    const a = shipment?.shipmentAddress || {}
    if (!(a.street && a.city && a.state && a.postalCode)) return 'The ship-to address is incomplete'
    if ((shipment?.shipmentRacks || []).some(r => !(r?.rackState > 2))) return 'Racks not complete'
    return null
}

// The order shipment's state as a label. Derived, never stored, so no surface can drift from another —
// this had three hand-synced copies before it lived here. State 2 splits on isShipmentReadyToShip,
// which is the ship gate itself. Both a chip and a text class are returned so callers pick a form
// rather than each mapping colours their own way.
export const orderShipmentStatus = (shipment) => {
    const state = shipment?.shipmentState || 0
    if (state > 2) return { label: 'Shipped', chip: 'chip-green', text: 'text-green' }
    if (state === 2) return isShipmentReadyToShip(shipment)
        ? { label: 'Ready', chip: 'chip-green', text: 'text-green' }
        : { label: 'In Progress', chip: 'chip-orange', text: 'text-orange' }
    if (state === 1) return { label: 'Queued', chip: 'chip-sky', text: 'text-sky' }
    return { label: 'Held', chip: 'chip-red', text: 'text-red' }
}

// Carrier named inside a free-text field. `Shipment.carrier` has no enum and is typed by hand or read off
// a label by the AI, so the stored value is anything from "FedEx" to "FEDEX GROUND 2DAY" to "Fedex US".
// Matched on WORD BOUNDARIES, never includes(): "ups" is a substring of GROUPS and PICKUPS, and a shipment
// note mentioning either would otherwise resolve as UPS and send a customer to the wrong carrier's site.
// USPS is tested before UPS so the order of this list can never make one shadow the other.
const CARRIER_TERMS = [
    { carrier: 'fedex', pattern: /\b(FEDEX|FED EX|FEDERAL EXPRESS)\b/ },
    { carrier: 'usps',  pattern: /\b(USPS|UNITED STATES POSTAL|POSTAL SERVICE)\b/ },
    { carrier: 'ups',   pattern: /\b(UPS|UNITED PARCEL)\b/ },
    { carrier: 'dhl',   pattern: /\bDHL\b/ }
]

// A UPS number identifies ITSELF: "1Z" + 16 alphanumerics, the only format among the four carriers that
// cannot be confused with another. Every other pattern is shared — a bare 12-digit number is FedEx
// Express or DHL or neither, and a 22-digit IMpb is USPS or a FedEx Ground Economy parcel injected into
// USPS — so no other number format is trusted to name a carrier on its own.
const UPS_SELF_IDENTIFYING = /^1Z[0-9A-Z]{16}$/

export const carrierFromText = (val) => {
    if (typeof val !== 'string' || !val) { return null }
    const text = val.toUpperCase()
    for (const { carrier, pattern } of CARRIER_TERMS) {
        if (pattern.test(text)) { return carrier }
    }
    return null
}

// Stored numbers are already compact uppercase (the schema setter normalizes every write), but an
// unsaved input value reaches here straight from the field, so normalize again rather than assume.
const normalizeTracking = (val) => String(val || '').toUpperCase().replace(/[^A-Z0-9]/g, '')

// Which carrier this shipment actually went with, or null when we cannot say. Ranked by how close the
// source is to what was really handed over: the typed/actual carrier, then what the AI read off the
// shipping label, then the label's service name ("FedEx Priority Overnight" names the carrier when the
// carrier field is blank), then the transaction receipt.
// orderRequestedType is deliberately NOT consulted. It is what the customer ASKED for on the order and
// the model says so — if they requested FedEx and it went UPS, it would name the wrong carrier with the
// same confidence as the real one.
// A self-identifying 1Z number fills in when no field names a carrier at all, and OVERRULES nothing: if
// a field says FedEx while the number is a 1Z, the two disagree about the same parcel and neither is
// trustworthy enough to hand a customer, so this returns null.
export const resolveCarrier = (shipment, trackingNumber) => {
    const named = carrierFromText(shipment?.carrier)
        || carrierFromText(shipment?.shippingLabel?.carrier)
        || carrierFromText(shipment?.shippingLabel?.service)
        || carrierFromText(shipment?.transactionRecord?.carrier)
    const number = normalizeTracking(trackingNumber)
    const selfIdentified = UPS_SELF_IDENTIFYING.test(number) ? 'ups' : null
    if (named && selfIdentified && named !== selfIdentified) { return null }
    return named || selfIdentified
}

// The number shapes each carrier actually issues, checked against the carrier we already resolved —
// these never GUESS a carrier, they only confirm the number could belong to the one we named. Several
// shapes are shared (a 22-digit number is a valid USPS IMpb and a valid FedEx Ground number), which is
// exactly why they are useless for identification and fine for confirmation.
// Deliberately STRICT: every carrier issues formats beyond these, and a legitimate number in a shape not
// listed here renders as plain text instead of a link. That is the intended trade — an absent link costs
// a copy-paste, a link onto a "not found" page costs a phone call about a parcel that is fine.
const CARRIER_NUMBER_FORMATS = {
    // "1Z" + 16. Mail Innovations and Freight print other formats, but those do not resolve on the
    // parcel tracking page this links to, so they are correctly excluded rather than merely unlisted.
    ups: [/^1Z[0-9A-Z]{16}$/],
    // Express 12, Ground 15, and the 20/22-digit forms Ground prints. Digits only.
    fedex: [/^\d{12}$/, /^\d{15}$/, /^\d{20}$/, /^\d{22}$/],
    // IMpb (20/22/26 digits) and the S10 international form, e.g. LZ123456789US.
    usps: [/^\d{20}$/, /^\d{22}$/, /^\d{26}$/, /^[A-Z]{2}\d{9}[A-Z]{2}$/],
    // Express air waybill. eCommerce numbers are excluded for the same reason as UPS Freight: they do
    // not resolve on the express tracking page this links to.
    dhl: [/^\d{10}$/, /^\d{11}$/]
}

const CARRIER_TRACKING_URLS = {
    fedex: 'https://www.fedex.com/fedextrack/?trknbr=',
    ups: 'https://www.ups.com/track?loc=en_US&tracknum=',
    usps: 'https://tools.usps.com/go/TrackConfirmAction?tLabels=',
    dhl: 'https://www.dhl.com/us-en/home/tracking/tracking-express.html?submit=1&tracking-id='
}

// The carrier's own tracking page for one number, or null when we are not confident enough to send
// anyone there. Null is the DESIGNED outcome, not a failure: the number is echoed to customers, and a
// link that lands on "not found" reads as us having lost a parcel that is moving perfectly well.
// Showing the number as plain text costs nothing by comparison.
// BOTH halves must hold — we must know the carrier, AND the number must be a shape that carrier issues.
// Knowing the carrier alone is not enough: a hand-typed number with a transposed or missing digit sits
// on a shipment whose carrier field is perfectly correct, and would otherwise link straight to a dead
// page. Callers pass a single number, so a multi-piece shipment links each parcel independently off the
// one carrier resolved for the shipment.
export const trackingLink = (shipment, trackingNumber) => {
    // A pickup was collected in person — no carrier took it and there is nothing to track. Tested on
    // method directly rather than through isOrderPickup, which is scoped to fulfillment orders; here the
    // question is only whether anything was handed to a carrier, which is true of an install pickup too.
    if (shipment?.method === 'pickup') { return null }
    const number = normalizeTracking(trackingNumber)
    if (!number) { return null }
    const carrier = resolveCarrier(shipment, number)
    if (!carrier) { return null }
    if (!(CARRIER_NUMBER_FORMATS[carrier] || []).some(format => format.test(number))) { return null }
    return `${CARRIER_TRACKING_URLS[carrier]}${encodeURIComponent(number)}`
}

// Announce a change to surfaces in THIS tab as well as other tabs/users.
//
// Use this instead of sendChangeEvent when something that must react is NOT on your parent chain —
// a modal over a page, a drill-down several modals deep, a sibling in another subtree. sendChangeEvent
// fires only local_data_change, which is the OUTBOUND pipe: the server never echoes it back, so your
// own tab's data_change_from_socket listeners never hear it.
//
//   data_change_from_socket — dispatched locally, right now. Every real listener binds this, so the
//                             current tab reacts exactly as it would to another user's change.
//   remote_data_change      — the socket leg. Send-only: nothing binds it locally, so surfaces that
//                             bind BOTH names fire exactly once rather than twice.
//
// Do NOT also call sendChangeEvent for the same change — that fires local_data_change too, and
// anything binding both names would react twice. See "Change events" in CLAUDE.md.
//
// `source` (optional) names the sender, for one that already applied the change from its API response. It
// rides ONLY this tab's copy, beside type and data rather than inside data (both copies share the data
// object), so isOwnBroadcast lets the sender skip its own event while the same change arriving from another
// tab, which never carries a source, still reaches it.
export const broadcastChange = (type, data, source = null) => {
    dispatchEvent(new CustomEvent('data_change_from_socket', { detail: source ? { type, data, source } : { type, data } }))
    dispatchEvent(new CustomEvent('remote_data_change', { detail: { type, data } }))
}

// True only for this tab's copy of a broadcastChange sent under `source`.
export const isOwnBroadcast = (e, source) => !!source && e?.detail?.source === source

// Project figures for lists that load projects by aggregation, where the model virtuals don't run. Each reads the
// stored fields the virtual reads. Server twins: the sales, hasRackServices and specialRates virtuals in the backend's
// models/Project.js, and the activeHold virtual in models/Organization.js — change them together.
export const projectSales = (project) => {
  return Array.isArray(project?.services)
    ? project.services.reduce((total, service) => total + (typeof service?.revenue === 'number' ? service.revenue : 0), 0)
    : 0
}

export const projectHasRackServices = (project) => {
  return Array.isArray(project?.services) && project.services.some(service =>
    [service?.category, service?.partNumber, service?.description].some(value => typeof value === 'string' && value.toLowerCase().includes('rack'))
  )
}

export const projectSpecialRates = (project) => {
  const services = Array.isArray(project?.services) ? project.services : []
  const hasSuffix = (suffix) => services.some(s => typeof s?.partNumber === 'string' && s.partNumber.toLowerCase().includes(suffix))
  const rates = {
    afterHours: hasSuffix('-ah'),
    travelRate: hasSuffix('-th'),
    doubleTime: hasSuffix('-dt'),
    prevailingWage: project?.isPrevailingWage === true || hasSuffix('-pw')
  }
  return { hasSpecialRates: Object.values(rates).some(Boolean), rates }
}

// The organization's open billing hold (no endDate), or null.
export const activeBillingHold = (organization) => {
  return Array.isArray(organization?.billingHolds) ? organization.billingHolds.find(hold => hold && !hold.endDate) ?? null : null
}

// Activity status for the Projects list. Activity = appointments, labor, receiving and shipments (the getProjects
// lookups); notes don't count and install dates never decide it. Something scheduled (a next appointment or shipment)
// means the project is never Inactive or idle. Paused hides the rest. Warehouse-type projects have no status.
export const PROJECT_IDLE_DAYS = 30
export const PROJECT_STATUS_RANK = { paused: 0, inactive: 1, idle: 2, notStarted: 3, active: 4 }
export const projectLastActivity = (p) => [
    ['Appointment', p?.lastAppointment?.appointmentTime], ['Labor', p?.lastLabor?.clockOut],
    ['Received', p?.lastReceived?.createdAt], ['Shipped', p?.lastShipped?.shipmentDate]
  ].filter(([, d]) => d).map(([label, d]) => ({ label, date: new Date(d) })).sort((a, b) => b.date - a.date)[0] || null
export const projectNextActivity = (p) => [
    ['Appointment', p?.nextAppointment?.appointmentTime], ['Shipment', p?.nextShipment?.shipmentDate]
  ].filter(([, d]) => d).map(([label, d]) => ({ label, date: new Date(d) })).sort((a, b) => a.date - b.date)[0] || null
export const projectActivityStatus = (p, now = new Date()) => {
  if (p?.pausedAt) return { key: 'paused' }
  const last = projectLastActivity(p), next = projectNextActivity(p)
  if (last) {
    const days = differenceInCalendarDays(now, last.date)
    return { key: (days <= PROJECT_IDLE_DAYS || next) ? 'active' : 'inactive', days, last, next }
  }
  const days = p?.createdAt ? differenceInCalendarDays(now, new Date(p.createdAt)) : 0
  return { key: (days <= PROJECT_IDLE_DAYS || next) ? 'notStarted' : 'idle', days, last: null, next }
}
// Next column wording: Today / Tomorrow / weekday inside the coming week / "MMM d" after that.
export const nextActivityLabel = (date, now = new Date()) => {
  const n = differenceInCalendarDays(date, now)
  return n <= 0 ? 'Today' : n === 1 ? 'Tomorrow' : n < 7 ? format(date, 'EEEE') : format(date, 'MMM d')
}
// Warehouse type, in the business vocabulary. Mirrors getProjectType (backend controllers/projects.js):
// isCustomerHold is Customer Stock; isCustomerReceivingHold is Customer Hold. null = an install project.
export const warehouseProjectType = (p) =>
  p?.isCustomerHold ? 'customerStock' : p?.isCustomerReceivingHold ? 'customerHold' : p?.isReceivingHold ? 'receivingHold'
  : p?.isInternalInventory ? 'internalInventory' : p?.isFulfillment ? 'fulfillment' : null
